import React, { useEffect, useState, useRef } from 'react';
import { Target, Lightbulb, Map, Brain, PlayCircle, ClipboardCheck, X, Check } from 'lucide-react';
export const ServicesSection = () => {
  const [activePopover, setActivePopover] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<'light' | 'plus'>('light');
  const popoverRef = useRef<HTMLDivElement>(null);
  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setActivePopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Reset selected option when popover changes
  useEffect(() => {
    setSelectedOption('light');
  }, [activePopover]);
  const togglePopover = (index: number) => {
    setActivePopover(prev => prev === index ? null : index);
  };
  const services = [{
    title: 'OKR Workshop',
    description: 'Align your team with meaningful objectives and measurable results.',
    icon: <Target className="w-10 h-10 text-gray-900" data-id="element-100" />,
    details: {
      objective: ['Create organizational alignment through shared objectives', 'Establish clear, measurable key results', 'Build accountability frameworks for tracking progress', 'Connect team activities to strategic priorities'],
      outcome: {
        shared: ['Complete set of OKRs for your organization or team', 'Clear ownership and responsibility assignments'],
        plus: ['Measurement criteria and tracking methodology', 'Implementation timeline with review cycles']
      },
      agenda: {
        shared: [{
          time: '9:00 AM',
          activity: 'Introduction to OKR methodology and principles'
        }, {
          time: '10:00 AM',
          activity: 'Current state assessment and goal identification'
        }, {
          time: '11:00 AM',
          activity: 'Objective definition workshop'
        }],
        plus: [{
          time: '12:00 PM',
          activity: 'Lunch break'
        }, {
          time: '1:00 PM',
          activity: 'Key results development and refinement'
        }, {
          time: '2:30 PM',
          activity: 'Implementation planning and next steps'
        }, {
          time: '4:00 PM',
          activity: 'Q&A and workshop conclusion'
        }]
      },
      lightVersion: 'Half-day remote workshop for up to 5 participants',
      plusVersion: 'Full-day in-person workshop for up to 10 participants with 30-day follow-up support'
    }
  }, {
    title: 'Product Definition Workshop',
    description: 'Transform ideas into well-defined products with clear user value and features.',
    icon: <Lightbulb className="w-10 h-10 text-gray-900" data-id="element-101" />,
    details: {
      objective: ['Convert abstract ideas into concrete product definitions', 'Establish clear value propositions for target users', 'Define core feature sets and requirements', 'Create alignment on product vision and goals'],
      outcome: {
        shared: ['Comprehensive product definition document', 'User personas and journey maps'],
        plus: ['Prioritized feature list and requirements', 'Initial product roadmap and development plan']
      },
      agenda: {
        shared: [{
          time: '9:00 AM',
          activity: 'Vision alignment and product goals discussion'
        }, {
          time: '10:30 AM',
          activity: 'User persona development and validation'
        }, {
          time: '12:00 PM',
          activity: 'Lunch break'
        }],
        plus: [{
          time: '1:00 PM',
          activity: 'Problem statement refinement and solution ideation'
        }, {
          time: '2:30 PM',
          activity: 'Feature prioritization and MVP definition'
        }, {
          time: '4:00 PM',
          activity: 'Implementation roadmap and next steps'
        }]
      },
      lightVersion: 'One-day remote workshop with pre-work and documentation',
      plusVersion: 'Two-day in-depth workshop with market analysis and competitive positioning'
    }
  }, {
    title: 'Roadmapping Sessions',
    description: 'Create strategic product roadmaps that align with business goals and timelines.',
    icon: <Map className="w-10 h-10 text-gray-900" data-id="element-102" />,
    details: {
      objective: ['Develop a clear, strategic product roadmap', 'Balance business goals with technical constraints', 'Align stakeholders around priorities and timelines', 'Create a flexible framework for decision-making'],
      outcome: {
        shared: ['Visual product roadmap with key initiatives', 'Prioritized features with dependencies mapped'],
        plus: ['Key milestones and delivery timelines', 'Resource allocation recommendations']
      },
      agenda: {
        shared: [{
          time: '9:00 AM',
          activity: 'Business goal setting and alignment'
        }, {
          time: '10:00 AM',
          activity: 'Initiative identification and brainstorming'
        }, {
          time: '11:00 AM',
          activity: 'Prioritization framework establishment'
        }],
        plus: [{
          time: '12:00 PM',
          activity: 'Lunch break'
        }, {
          time: '1:00 PM',
          activity: 'Timeline development and resource planning'
        }, {
          time: '2:30 PM',
          activity: 'Stakeholder alignment and feedback integration'
        }, {
          time: '3:30 PM',
          activity: 'Finalization and next steps'
        }]
      },
      lightVersion: 'Half-day roadmap creation session with digital deliverables',
      plusVersion: 'Full-day session with quarterly planning and stakeholder presentation materials'
    }
  }, {
    title: 'Process Audit',
    description: 'Evaluate and optimize your product development process for efficiency and quality.',
    icon: <ClipboardCheck className="w-10 h-10 text-gray-900" data-id="element-103" />,
    details: {
      objective: ['Identify bottlenecks and inefficiencies in current processes', 'Evaluate team structure and collaboration patterns', 'Assess alignment between process and business goals', 'Identify opportunities for quality and speed improvements'],
      outcome: {
        shared: ['Comprehensive audit report with findings', 'Prioritized recommendations for process optimization'],
        plus: ['Implementation plan with quick wins and long-term improvements', 'Metrics framework to measure process effectiveness']
      },
      agenda: {
        shared: [{
          time: '9:00 AM',
          activity: 'Current process review and mapping'
        }, {
          time: '10:30 AM',
          activity: 'Team interviews and collaboration assessment'
        }, {
          time: '12:00 PM',
          activity: 'Lunch break'
        }],
        plus: [{
          time: '1:00 PM',
          activity: 'Process analysis and bottleneck identification'
        }, {
          time: '2:30 PM',
          activity: 'Improvement opportunity prioritization'
        }, {
          time: '3:30 PM',
          activity: 'Implementation planning and next steps'
        }, {
          time: '4:30 PM',
          activity: 'Presentation of findings and recommendations'
        }]
      },
      lightVersion: 'Remote audit with documentation review and key stakeholder interviews',
      plusVersion: 'Comprehensive on-site audit with team workshops and implementation support'
    }
  }, {
    title: 'AI Strategy Assessment',
    description: 'Identify high-value AI opportunities and implementation pathways for your business.',
    icon: <Brain className="w-10 h-10 text-gray-900" data-id="element-104" />,
    details: {
      objective: ['Identify strategic opportunities for AI implementation', 'Assess organizational readiness for AI adoption', 'Evaluate technical capabilities and infrastructure needs', 'Prioritize use cases based on business impact'],
      outcome: {
        shared: ['Comprehensive assessment report with findings', 'Prioritized AI use cases with impact estimates'],
        plus: ['Capability gap analysis and recommendations', 'Implementation roadmap with resource requirements']
      },
      agenda: {
        shared: [{
          time: '9:00 AM',
          activity: 'Business objectives review and prioritization'
        }, {
          time: '10:30 AM',
          activity: 'Current state analysis of data and systems'
        }, {
          time: '12:00 PM',
          activity: 'Lunch break'
        }],
        plus: [{
          time: '1:00 PM',
          activity: 'AI opportunity identification workshop'
        }, {
          time: '2:30 PM',
          activity: 'Capability assessment and gap analysis'
        }, {
          time: '3:30 PM',
          activity: 'Risk evaluation and mitigation strategies'
        }, {
          time: '4:30 PM',
          activity: 'Prioritization and next steps planning'
        }]
      },
      lightVersion: 'Remote assessment with documentation and recommendations report',
      plusVersion: 'In-depth assessment with stakeholder interviews and detailed implementation roadmap'
    }
  }, {
    title: 'AI Strategy Execution Plan',
    description: 'Bridge the gap between AI strategy and execution with clear, actionable steps.',
    icon: <PlayCircle className="w-10 h-10 text-gray-900" data-id="element-105" />,
    details: {
      objective: ['Create detailed execution plans for AI initiatives', 'Define clear milestones and success metrics', 'Identify resource requirements and constraints', 'Develop risk mitigation strategies'],
      outcome: {
        shared: ['Comprehensive execution plan with timelines', 'Resource allocation and responsibility matrix'],
        plus: ['Technical architecture and integration approach', 'Success metrics and measurement framework']
      },
      agenda: {
        shared: [{
          time: '9:00 AM',
          activity: 'Initiative scoping and goal alignment'
        }, {
          time: '10:30 AM',
          activity: 'Resource planning and team composition'
        }, {
          time: '12:00 PM',
          activity: 'Lunch break'
        }],
        plus: [{
          time: '1:00 PM',
          activity: 'Technical architecture review and planning'
        }, {
          time: '2:30 PM',
          activity: 'Implementation phasing and milestone definition'
        }, {
          time: '3:30 PM',
          activity: 'Success metric definition and tracking approach'
        }, {
          time: '4:30 PM',
          activity: 'Risk mitigation planning and contingencies'
        }]
      },
      lightVersion: 'Execution plan development for a single AI initiative',
      plusVersion: 'Comprehensive execution plan for multiple initiatives with integration strategy'
    }
  }];
  return <section id="services" className="w-full py-24 bg-white" data-id="element-106">
      <div className="container mx-auto px-6" data-id="element-107">
        <div className="mb-16" data-id="element-108">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-109">
            Services
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl" data-id="element-110">
            Specialized workshops and strategic sessions to elevate your product
            strategy.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 relative" data-id="element-111">
          {services.map((service, index) => <div key={index} className="bg-white border-t border-gray-200 pt-8 group" data-id="element-112">
              <div className="flex items-center gap-3 mb-3" data-id="element-113">
                {service.icon}
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-700 transition-colors" data-id="element-114">
                  {service.title}
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed mb-4 text-sm" data-id="element-115">
                {service.description}
              </p>
              <button onClick={() => togglePopover(index)} className="inline-flex items-center text-blue-700 hover:text-blue-900 transition-colors font-medium text-sm" data-id="element-116">
                Learn more
              </button>
            </div>)}
          {/* Popover */}
          {activePopover !== null && <div ref={popoverRef} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" style={{
          animation: 'fadeIn 0.25s ease-out'
        }} data-id="element-117">
              <div className="bg-white p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl" style={{
            animation: 'zoomOut 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            transformOrigin: 'center'
          }} data-id="element-118">
                {/* Header with icon */}
                <div className="flex justify-between items-start mb-8" data-id="element-119">
                  <div className="flex items-center gap-4" data-id="element-120">
                    <div className="bg-gray-50 p-3 rounded-full" data-id="element-121">
                      {services[activePopover].icon}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900" data-id="element-122">
                      {services[activePopover].title}
                    </h3>
                  </div>
                  <button onClick={() => setActivePopover(null)} className="text-gray-500 hover:text-gray-900 transition-colors" data-id="element-123">
                    <X size={24} data-id="element-124" />
                  </button>
                </div>
                {/* Service options */}
                <div className="mb-8 border-b border-gray-200 pb-8" data-id="element-125">
                  <h4 className="font-semibold text-gray-900 mb-4 text-lg" data-id="element-126">
                    Service Options
                  </h4>
                  <div className="grid grid-cols-2 gap-4" data-id="element-127">
                    <button onClick={() => setSelectedOption('light')} className={`flex flex-col items-center p-4 border rounded-md transition-all ${selectedOption === 'light' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-128">
                      <div className="mb-2 flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-200" data-id="element-129">
                        {selectedOption === 'light' && <Check className="w-5 h-5 text-blue-600" data-id="element-130" />}
                      </div>
                      <h5 className="font-medium text-gray-900" data-id="element-131">Light</h5>
                      <p className="text-xs text-center text-gray-600 mt-1" data-id="element-132">
                        {services[activePopover].details.lightVersion}
                      </p>
                    </button>
                    <button onClick={() => setSelectedOption('plus')} className={`flex flex-col items-center p-4 border rounded-md transition-all ${selectedOption === 'plus' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-133">
                      <div className="mb-2 flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-200" data-id="element-134">
                        {selectedOption === 'plus' && <Check className="w-5 h-5 text-blue-600" data-id="element-135" />}
                      </div>
                      <h5 className="font-medium text-gray-900" data-id="element-136">Plus</h5>
                      <p className="text-xs text-center text-gray-600 mt-1" data-id="element-137">
                        {services[activePopover].details.plusVersion}
                      </p>
                    </button>
                  </div>
                </div>
                <div className="space-y-8" data-id="element-138">
                  {/* Objectives */}
                  <div data-id="element-139">
                    <h4 className="font-semibold text-gray-900 mb-4" data-id="element-140">
                      Objectives
                    </h4>
                    <ul className="grid grid-cols-2 gap-3" data-id="element-141">
                      {services[activePopover].details.objective.map((point, i) => <li key={i} className="flex items-start gap-2" data-id="element-142">
                            <div className="mt-1 flex-shrink-0" data-id="element-143">
                              <Check className="w-4 h-4 text-blue-600" data-id="element-144" />
                            </div>
                            <span className="text-gray-700 text-sm" data-id="element-145">
                              {point}
                            </span>
                          </li>)}
                    </ul>
                  </div>
                  {/* Outcomes */}
                  <div data-id="element-146">
                    <h4 className="font-semibold text-gray-900 mb-4" data-id="element-147">
                      Outcomes
                    </h4>
                    {/* Light Option Outcomes */}
                    {selectedOption === 'light' && <div className="bg-gray-50 p-4 rounded-md" data-id="element-148">
                        <h5 className="font-medium text-blue-700 mb-3 text-sm uppercase" data-id="element-149">
                          Light Package Deliverables
                        </h5>
                        <ul className="space-y-2" data-id="element-150">
                          {services[activePopover].details.outcome.shared.map((point, i) => <li key={i} className="flex items-start gap-2" data-id="element-151">
                                <div className="mt-1 flex-shrink-0" data-id="element-152">
                                  <Check className="w-4 h-4 text-blue-600" data-id="element-153" />
                                </div>
                                <span className="text-gray-700" data-id="element-154">{point}</span>
                              </li>)}
                          <li className="flex items-start gap-2 mt-4 pt-4 border-t border-gray-200" data-id="element-155">
                            <div className="mt-1 flex-shrink-0" data-id="element-156">
                              <X className="w-4 h-4 text-gray-400" data-id="element-157" />
                            </div>
                            <span className="text-gray-500 text-sm italic" data-id="element-158">
                              Additional deliverables available in the Plus
                              package
                            </span>
                          </li>
                        </ul>
                      </div>}
                    {/* Plus Option Outcomes */}
                    {selectedOption === 'plus' && <div className="bg-blue-50 p-4 rounded-md" data-id="element-159">
                        <h5 className="font-medium text-blue-700 mb-3 text-sm uppercase" data-id="element-160">
                          Plus Package Deliverables
                        </h5>
                        <ul className="space-y-2" data-id="element-161">
                          {services[activePopover].details.outcome.shared.map((point, i) => <li key={i} className="flex items-start gap-2" data-id="element-162">
                                <div className="mt-1 flex-shrink-0" data-id="element-163">
                                  <Check className="w-4 h-4 text-blue-600" data-id="element-164" />
                                </div>
                                <span className="text-gray-700" data-id="element-165">{point}</span>
                              </li>)}
                          <li className="flex items-start gap-0 mt-2 mb-2" data-id="element-166">
                            <div className="w-6" data-id="element-167"></div>
                            <span className="text-blue-700 font-medium text-sm" data-id="element-168">
                              Plus additional deliverables:
                            </span>
                          </li>
                          {services[activePopover].details.outcome.plus.map((point, i) => <li key={i} className="flex items-start gap-2 pl-6" data-id="element-169">
                                <div className="mt-1 flex-shrink-0" data-id="element-170">
                                  <Check className="w-4 h-4 text-blue-600" data-id="element-171" />
                                </div>
                                <span className="text-gray-700" data-id="element-172">{point}</span>
                              </li>)}
                        </ul>
                      </div>}
                  </div>
                  {/* Agenda */}
                  <div data-id="element-173">
                    <div className="flex justify-between items-center mb-4" data-id="element-174">
                      <h4 className="font-semibold text-gray-900" data-id="element-175">
                        {selectedOption === 'light' ? 'Light Package Agenda' : 'Plus Package Agenda'}
                      </h4>
                      <span className="text-xs text-gray-500 italic" data-id="element-176">
                        *Schedule will be adjusted to your specific needs
                      </span>
                    </div>
                    <div className="space-y-3 border-l-2 border-gray-200 pl-4" data-id="element-177">
                      {services[activePopover].details.agenda.shared.map((item, i) => <div key={i} className="flex" data-id="element-178">
                            <div className="w-24 flex-shrink-0 font-medium text-gray-900" data-id="element-179">
                              {item.time}
                            </div>
                            <div className="text-gray-700" data-id="element-180">{item.activity}</div>
                          </div>)}
                      {selectedOption === 'plus' && <>
                          {services[activePopover].details.agenda.plus.map((item, i) => <div key={i} className="flex" data-id="element-181">
                                <div className="w-24 flex-shrink-0 font-medium text-gray-900" data-id="element-182">
                                  {item.time}
                                </div>
                                <div className="text-gray-700" data-id="element-183">
                                  {item.activity}
                                </div>
                              </div>)}
                        </>}
                      {selectedOption === 'light' && <div className="flex pt-2 text-gray-500 italic text-sm" data-id="element-184">
                          <div className="w-24 flex-shrink-0" data-id="element-185"></div>
                          <div data-id="element-186">
                            Additional sessions available in the Plus package
                          </div>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>}
        </div>
      </div>
      {/* Animation styles */}
      <style jsx data-id="element-187">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        @keyframes zoomOut {
          from {
            transform: scale(0.8);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </section>;
};